import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { requestsAPI } from '../../services/api';
import { FiPlus, FiSearch, FiFilter, FiRefreshCw } from 'react-icons/fi';
import { LuEye, LuPencil } from 'react-icons/lu';
import { format } from 'date-fns';
import { statusColors } from '../../utils/statusColors';
import { formatStatus } from '../../utils/formatters';
import LoadingSpinner from '../../components/LoadingSpinner';
import ListPaginationControls from '../../components/ListPaginationControls';

const RequestsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dateRange: 'all'
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Parse URL parameters and set initial filters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get('type');

    if (typeParam) {
      const types = typeParam.split(',');
      setFilters(prev => ({
        ...prev,
        type: types.length === 1 ? types[0] : typeParam
      }));
    } else {
      // Clear type filter when navigating to "All Requests"
      setFilters(prev => ({ ...prev, type: '' }));
    }
    setPage(1);
  }, [location.search]);

  // Get page title based on URL filters
  const getPageTitle = () => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get('type');
    
    if (typeParam === 'incoming' || typeParam === 'incoming,crossdock' || typeParam === 'incoming,inbound_manifest' || typeParam === 'incoming,crossdock,inbound_manifest') {
      return 'My Inbound Requests';
    } else if (typeParam === 'ship') {
      return 'My Outbound Requests';
    } else if (typeParam && (typeParam.includes('pick_pack') || typeParam.includes('kit_build'))) {
      return 'My Orders';
    }
    
    return 'My Requests';
  };

  // Get page description based on URL filters
  const getPageDescription = () => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get('type');
    
    if (typeParam === 'incoming' || typeParam === 'incoming,crossdock' || typeParam === 'incoming,inbound_manifest' || typeParam === 'incoming,crossdock,inbound_manifest') {
      return 'Your incoming inventory, receiving requests (ASNs), inbound manifests, and cross-dock operations';
    } else if (typeParam === 'ship') {
      return 'Your shipping and outbound fulfillment requests';
    } else if (typeParam && typeParam.includes('kit_build')) {
      return 'Your kit building requests';
    }
    
    return 'A list of all your service requests including their status, type, and created date.';
  };

  // Fetch requests
  const { data, isLoading, isError, refetch } = useQuery(
    ['requests', page, searchTerm, filters],
    () => requestsAPI.getCustomerRequests({
      page,
      limit,
      search: searchTerm,
      ...filters
    }),
    {
      keepPreviousData: true
    }
  );

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setFilters({
      ...filters,
      status: e.target.value
    });
    setPage(1);
  };

  // Handle type filter change
  const handleTypeFilterChange = (e) => {
    setFilters({
      ...filters,
      type: e.target.value
    });
    setPage(1);
  };

  // Handle date range filter change
  const handleDateRangeFilterChange = (e) => {
    setFilters({
      ...filters,
      dateRange: e.target.value
    });
    setPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      dateRange: 'all'
    });
    setSearchTerm('');
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [limit]);

  // Get the request type display name
  const getRequestTypeName = (type) => {
    switch (type) {
      case 'ship':
      case 'shipping':
        return 'Shipping';
      case 'incoming':
        return 'Incoming Inventory';
      case 'crossdock':
        return 'Cross-Dock';
      case 'inbound_manifest':
        return 'Inbound Manifest';
      case 'pick_pack':
        return 'Pick & Pack';
      case 'kit_build':
      case 'kitbuild':
        return 'Kit Building';
      default:
        return type;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">{getPageTitle()}</h1>
          <p className="mt-2 text-sm text-gray-700">{getPageDescription()}</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              const urlParams = new URLSearchParams(location.search);
              const typeParam = urlParams.get('type');
              
              if (typeParam === 'incoming' || typeParam === 'incoming,crossdock' || typeParam === 'incoming,inbound_manifest' || typeParam === 'incoming,crossdock,inbound_manifest') {
                navigate('/customer/requests/new?type=incoming');
              } else if (typeParam === 'ship') {
                navigate('/customer/requests/new?type=ship');
              } else {
                navigate('/customer/requests/new');
              }
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <FiPlus className="mr-2" />
            New Request
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="mt-6 mb-8">
        {/* Search bar */}
        <div className="mb-4">
          <form onSubmit={handleSearch} className="relative rounded-md shadow-sm max-w-md">
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search requests, including manifest name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </form>
        </div>
        
        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <FiFilter className="mr-2 text-gray-500" />
            <label htmlFor="status-filter" className="sr-only">Filter by status</label>
            <select
              id="status-filter"
              value={filters.status}
              onChange={handleStatusFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="type-filter" className="sr-only">Filter by type</label>
            <select
              id="type-filter"
              value={filters.type}
              onChange={handleTypeFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="">All Types</option>
              <option value="shipping">Shipping</option>
              <option value="incoming">Incoming Inventory</option>
              <option value="inbound_manifest">Inbound Manifest</option>
              <option value="crossdock">Cross-Dock</option>
              <option value="pickpack">Pick & Pack</option>
              <option value="kitbuild">Kit Building</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="date-filter" className="sr-only">Filter by date</label>
            <select
              id="date-filter"
              value={filters.dateRange}
              onChange={handleDateRangeFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear
            </button>
            
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiRefreshCw className="mr-1 h-3 w-3" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Requests list */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                There was an error loading your requests. Please try again later.
              </p>
            </div>
          </div>
        </div>
      ) : (!data?.data || data.data.length === 0) ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md mt-4">
          <div className="px-4 py-5 sm:p-6 text-center">
            <p className="text-gray-500 text-base">No requests found.</p>
            <p className="text-gray-400 text-sm mt-1">Adjust your filters or create a new request.</p>
          </div>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Request Number</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Updated</th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data?.data?.map((request) => (
                <tr key={request.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    <Link
                      to={`/customer/requests/${request.id}`}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      {request.requestNumber || request.id}
                    </Link>
                    {request.requestType === 'inbound_manifest' && request.manifestName && (
                      <div className="mt-1 text-xs font-normal text-gray-500">
                        Manifest Name: {request.manifestName}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {getRequestTypeName(request.requestType || request.type)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[request.status] || 'bg-gray-100 text-gray-800'}`}>
                      {formatStatus(request.status, request.requestType || request.type)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {request.createdAt ? format(new Date(request.createdAt), 'MMM d, yyyy') : 'N/A'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {request.updatedAt ? format(new Date(request.updatedAt), 'MMM d, yyyy') : 'N/A'}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/customer/requests/${request.id}`}
                        className="text-gray-500 hover:text-gray-700"
                        title="View Request"
                      >
                        <LuEye className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/customer/requests/${request.id}/edit`}
                        className="text-gray-500 hover:text-gray-700"
                        title="Edit Request"
                      >
                        <LuPencil className="h-4 w-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <ListPaginationControls
          currentPage={page}
          totalPages={data.totalPages || 1}
          totalItems={data.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};

export default RequestsList; 
